import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useStores } from "@stores";
import type { Token } from "@stores/BalanceStore";

import { BN } from "@utils/BN";

export type TradeDirection = "long" | "short";
export type OrderType = "limit" | "market";
export type TradeboxTimeInForce = "GTC" | "IOC" | "FOK" | "PostOnly" | "GTD";

export const MIN_GTD_LEAD_MS = 60_000;

export const getDefaultGtdExpiryMs = (): number => {
  return Date.now() + MIN_GTD_LEAD_MS;
};

export interface TpSlData {
  takeProfitPrice: BN;
  gainPercent: BN;
  stopLossPrice: BN;
  lossPercent: BN;
}

export interface TradeboxInputs {
  limitPrice: BN;
  size: BN;
  total: BN;
  direction: TradeDirection;
  slider: number;
  userTradeableBalance: Pick<Token, "amount" | "amountFormatted">;
  userLeveragableBalance: Pick<Token, "amount" | "amountFormatted">;
  availableMargin: Pick<Token, "amount" | "amountFormatted">;
  orderType: OrderType;
  leverage: number;
  setLimitPrice: (v: BN) => void;
  setSize: (v: BN) => void;
  setTotal: (v: BN) => void;
  setDirection: (d: TradeDirection) => void;
  setSlider: (n: number) => void;
  setOrderType: (t: OrderType) => void;
  setLeverage: (v: number) => void;
  validate: TradeboxValidation;
  create: (timeInForce?: TradeboxTimeInForce, reduceOnly?: boolean, gtdExpireAtMs?: number) => Promise<void>;

  isLeverageDialogOpen: boolean;
  setIsLeverageDialogOpen: (v: boolean) => void;

  tpSlData: TpSlData;
  setTpSlData: (data: TpSlData) => void;
}

export interface TradeboxValidation {
  limitPrice: boolean;
  size: boolean;
  total: boolean;
  empty: boolean;
}

export const useTradeboxInputs = (
  initialDirection: TradeDirection = "long",
  initialOrderType: OrderType = "limit",
): TradeboxInputs => {
  const { balanceStore, marketStore, tradeboxStore, accountStore } = useStores();

  // NOTE: intentionally NOT destructuring marketPrice here.
  // Accessing marketStore.marketPrice during render causes MobX to track it,
  // re-rendering the parent observer on every price tick.
  // Instead, access marketStore.marketPrice lazily inside callbacks/effects only.
  const { baseToken, quoteToken, activeMarket } = marketStore;

  const currentLeverage = marketStore.currentLeverage;

  const [limitPrice, setLimitPriceState] = useState(BN.ZERO);
  const [size, setSizeState] = useState(BN.ZERO);
  const [total, setTotalState] = useState(BN.ZERO);
  const [direction, setDirection] = useState<TradeDirection>(initialDirection);
  const [slider, setSliderState] = useState(0);
  const [orderType, setOrderType] = useState<OrderType>(initialOrderType);
  const [isLeverageDialogOpen, setIsLeverageDialogOpen] = useState(false);
  const [tpSlData, setTpSlData] = useState<TpSlData>({
    takeProfitPrice: BN.ZERO,
    gainPercent: BN.ZERO,
    stopLossPrice: BN.ZERO,
    lossPercent: BN.ZERO,
  });

  const [lastMarketAddr, setLastMarketAddr] = useState<string | null>(null);

  useEffect(() => {
    if (!activeMarket || !baseToken || !quoteToken) return;

    if (activeMarket.addr !== lastMarketAddr) {
      setLastMarketAddr(activeMarket.addr);
      setLimitPriceState(new BN(marketStore.marketPrice));
      setSizeState(BN.ZERO);
      setTotalState(BN.ZERO);
      setSliderState(0);
      setTpSlData({
        takeProfitPrice: BN.ZERO,
        gainPercent: BN.ZERO,
        stopLossPrice: BN.ZERO,
        lossPercent: BN.ZERO,
      });
    }
  }, [activeMarket?.addr, lastMarketAddr, marketStore]);

  useEffect(() => {
    if (tradeboxStore.requiredPrice !== null) {
      const newPrice = new BN(tradeboxStore.requiredPrice);
      if (!limitPrice.eq(newPrice)) {
        setLimitPriceState(newPrice);
      }
    }
  }, [tradeboxStore.requiredPrice]);

  useEffect(() => {
    setSizeState(BN.ZERO);
    setTotalState(BN.ZERO);
    setSliderState(0);
    setTpSlData({
      takeProfitPrice: BN.ZERO,
      gainPercent: BN.ZERO,
      stopLossPrice: BN.ZERO,
      lossPercent: BN.ZERO,
    });
  }, [currentLeverage]);

  const userTradeableBalance = quoteToken
    ? balanceStore.getVaultBalanceByAddress(quoteToken.address)
    : { amount: BN.ZERO, amountFormatted: "0" };

  const userLeveragableBalance = quoteToken
    ? balanceStore.getVaultBalanceByAddressWithLeverage(quoteToken.address)
    : { amount: BN.ZERO, amountFormatted: "0" };

  const portfolio = accountStore.portfolio;
  const maintRequiredDep = portfolio?.total_position_mm;
  const availableMargin = useMemo(() => {
    if (portfolio) {
      const availableValue = new BN(portfolio.available_balance);
      return {
        amount: availableValue,
        amountFormatted: availableValue.toFormat(2),
      };
    }
    return { amount: BN.ZERO, amountFormatted: "0" };
  }, [portfolio]);

  // ── Stable refs ──────────────────────────────────────────────────────
  // Break the cascade: portfolio/equity/token changes → callback recreation → UI re-renders.
  // Callbacks read the CURRENT value via ref when invoked, but their identity stays stable.
  const totalRef = useRef(total);
  totalRef.current = total;

  const limitPriceRef = useRef(limitPrice);
  limitPriceRef.current = limitPrice;

  const orderTypeRef = useRef(orderType);
  orderTypeRef.current = orderType;

  const baseTokenRef = useRef(baseToken);
  baseTokenRef.current = baseToken;

  const quoteTokenRef = useRef(quoteToken);
  quoteTokenRef.current = quoteToken;

  const sizeRef = useRef(size);
  sizeRef.current = size;

  const directionRef = useRef(direction);
  directionRef.current = direction;

  const tpSlDataRef = useRef(tpSlData);
  tpSlDataRef.current = tpSlData;

  const availableMarginRef = useRef(availableMargin);
  availableMarginRef.current = availableMargin;

  const setLimitPrice = useCallback(
    (v: BN) => {
      if (!baseTokenRef.current || !quoteTokenRef.current) return;
      setLimitPriceState(v);
      const priceToUse = orderTypeRef.current === "market" ? new BN(marketStore.marketPrice) : v;

      // Always keep total fixed, recalculate size
      if (!priceToUse.isZero()) {
        setSizeState(totalRef.current.div(priceToUse));
      }
    },
    [marketStore],
  );

  const computeMaxTotal = useCallback((): BN => {
    // Budget-based cap (old): X * (1/leverage + feeRate) <= available
    const feeRateRaw = marketStore.activeMarket?.taker_fee_rate ?? 0;
    const feeRate = new BN(feeRateRaw).div(1_000_000);
    const invLev = new BN(1).div(currentLeverage);
    const budgetDenom = feeRate.plus(invLev);
    const budgetLimit = budgetDenom.isZero() ? BN.ZERO : availableMargin.amount.div(budgetDenom);
    // Risk-based cap (new): ensure post-trade cross account is NOT liquidatable
    // equity - feeRate*X >= maint_current + maint_ratio*X
    // => X <= (equity - maint_current) / (feeRate + maint_ratio)
    const equity = new BN(portfolio?.equity ?? 0);
    const maintCurrentRaw = portfolio?.total_position_mm ?? 0;
    const maintCurrent = new BN(maintCurrentRaw);
    const maintRatioRaw = marketStore.activeMarket?.maintenance_margin_ratio ?? "0";
    const maintRatio = new BN(Number(maintRatioRaw)).div(1_000_000);
    const riskNumerator = equity.minus(maintCurrent);
    const riskDenom = feeRate.plus(maintRatio);
    let riskLimit = BN.ZERO;
    if (riskNumerator.gt(0) && !riskDenom.isZero()) {
      // Add small safety buffer to reduce race with mark/funding updates
      const safety = new BN(0.98);
      riskLimit = riskNumerator.div(riskDenom).times(safety);
    }
    // Use the stricter of the two limits; never negative
    const maxTotal = BN.max(BN.ZERO, BN.min(budgetLimit, riskLimit));
    return maxTotal;
  }, [
    availableMargin.amount,
    currentLeverage,
    marketStore.activeMarket?.taker_fee_rate,
    marketStore.activeMarket?.maintenance_margin_ratio,
    portfolio?.equity,
    maintRequiredDep,
  ]);

  const computeMaxTotalRef = useRef(computeMaxTotal);
  computeMaxTotalRef.current = computeMaxTotal;

  const _setSliderValue = useCallback((v: BN) => {
    const maxTotal = computeMaxTotalRef.current();
    const sliderValue = v.div(maxTotal).times(100).dp(0);
    const clampedValue = BN.clamp(sliderValue.isFinite() ? sliderValue : BN.ZERO, 0, 100).toNumber();

    setSliderState(clampedValue);
  }, []);

  const setSize = useCallback(
    (v: BN) => {
      if (!baseTokenRef.current || !quoteTokenRef.current) return;
      setSizeState(v);

      const priceToUse = orderTypeRef.current === "market" ? new BN(marketStore.marketPrice) : limitPriceRef.current;
      const total = v.times(priceToUse);

      setTotalState(total);

      const maxTotal = computeMaxTotalRef.current();
      const sliderValue = total.div(maxTotal).times(100).dp(0);
      const clampedValue = BN.clamp(sliderValue.isFinite() ? sliderValue : BN.ZERO, 0, 100).toNumber();

      setSliderState(clampedValue);
    },
    [marketStore],
  );

  const setTotal = useCallback(
    (v: BN, ignoreSlider = false) => {
      if (!baseTokenRef.current || !quoteTokenRef.current) return;
      setTotalState(v);

      const priceToUse = orderTypeRef.current === "market" ? new BN(marketStore.marketPrice) : limitPriceRef.current;
      if (!priceToUse.isZero()) {
        setSizeState(v.div(priceToUse));

        if (!ignoreSlider) {
          _setSliderValue(v);
        }
      }
    },
    [_setSliderValue, marketStore],
  );

  const setSlider = useCallback(
    (n: number) => {
      const clamped = BN.clamp(n, 0, 100);
      setSliderState(clamped.toNumber());
      if (availableMarginRef.current?.amount.gt(BN.ZERO)) {
        const percent = clamped.div(100);
        const maxTotal = computeMaxTotalRef.current();
        const newTotal = maxTotal.times(percent);
        setTotal(newTotal, true);
      }
    },
    [setTotal],
  );

  const setLeverage = useCallback((v: number) => {
    const clamped = BN.clamp(v, 1, 10000);
    tradeboxStore.setMarketLeverage(clamped.toNumber());
  }, []);

  const validate = useMemo<TradeboxValidation>(() => {
    const maxTotal = computeMaxTotal();
    const isLimit = orderType === "limit";
    return {
      limitPrice: isLimit && limitPrice.lt(BN.ZERO),
      size: size.lt(BN.ZERO) || total.gt(maxTotal),
      total: total.lt(BN.ZERO) || total.gt(maxTotal),
      empty: (isLimit && limitPrice.isZero()) || size.isZero() || total.isZero(),
    };
  }, [limitPrice, size, total, orderType, computeMaxTotal]);

  // Ref for validate — so `create` doesn't recreate on every portfolio update
  const validateRef = useRef(validate);
  validateRef.current = validate;

  useEffect(() => {
    if (!activeMarket) {
      tradeboxStore.setChartTpSlPreview(null);
      return;
    }

    const side = direction === "long" ? "buy" : "sell";
    const entryBn = orderType === "market" ? new BN(marketStore.marketPrice) : limitPrice;
    const { takeProfitPrice, stopLossPrice } = tpSlData;

    const hasTp = takeProfitPrice.gt(0) && (side === "buy" ? takeProfitPrice.gt(entryBn) : takeProfitPrice.lt(entryBn));
    const hasSl = stopLossPrice.gt(0) && (side === "buy" ? stopLossPrice.lt(entryBn) : stopLossPrice.gt(entryBn));

    if (!hasTp && !hasSl) {
      tradeboxStore.setChartTpSlPreview(null);
      return;
    }

    // Same numeric scale as `MarketStore.marketPrice` / kline OHLC (API float), not quote-token raw decimals.
    const toChartPrice = (bn: BN) => bn.toNumber();

    const gainPctDerived = hasTp
      ? side === "buy"
        ? takeProfitPrice.minus(entryBn).div(entryBn).times(100)
        : entryBn.minus(takeProfitPrice).div(entryBn).times(100)
      : BN.ZERO;
    const lossPctDerived = hasSl
      ? side === "buy"
        ? entryBn.minus(stopLossPrice).div(entryBn).times(100)
        : stopLossPrice.minus(entryBn).div(entryBn).times(100)
      : BN.ZERO;

    const gainPercentValue = hasTp
      ? (tpSlData.gainPercent.gt(0) ? tpSlData.gainPercent : gainPctDerived).toNumber()
      : undefined;
    const lossPercentValue = hasSl
      ? (tpSlData.lossPercent.gt(0) ? tpSlData.lossPercent : lossPctDerived).toNumber()
      : undefined;

    tradeboxStore.setChartTpSlPreview({
      entry: toChartPrice(entryBn),
      takeProfit: hasTp ? toChartPrice(takeProfitPrice) : null,
      stopLoss: hasSl ? toChartPrice(stopLossPrice) : null,
      direction,
      gainPercent: gainPercentValue,
      lossPercent: lossPercentValue,
      // Always derive from current size (base); size is kept in sync with total in quote mode.
      baseSize: size.gt(0) ? size.toNumber() : undefined,
    });
  }, [
    activeMarket,
    tradeboxStore,
    direction,
    orderType,
    limitPrice,
    size,
    total,
    tpSlData.takeProfitPrice,
    tpSlData.stopLossPrice,
    tpSlData.gainPercent,
    tpSlData.lossPercent,
    marketStore.marketPrice,
  ]);

  const create = useCallback(
    (timeInForce: TradeboxTimeInForce = "GTC", reduceOnly = false, gtdExpireAtMs?: number) => {
      if (!baseTokenRef.current || !quoteTokenRef.current) return Promise.resolve();

      const currentValidate = validateRef.current;
      if (currentValidate.limitPrice || currentValidate.size || currentValidate.total || currentValidate.empty) {
        return Promise.reject(new Error("Invalid trade inputs"));
      }

      if (
        timeInForce === "GTD" &&
        (gtdExpireAtMs == null || !Number.isFinite(gtdExpireAtMs) || gtdExpireAtMs <= Date.now() + MIN_GTD_LEAD_MS)
      ) {
        return Promise.reject(new Error("Invalid GTD expiry"));
      }

      const price = orderTypeRef.current === "market" ? new BN(marketStore.marketPrice) : limitPriceRef.current;

      return tradeboxStore
        .openOrder({
          size: sizeRef.current,
          price,
          side: directionRef.current === "long" ? "buy" : "sell",
          type: orderTypeRef.current,
          timeInForce,
          gtdExpireAtMs: timeInForce === "GTD" ? gtdExpireAtMs : undefined,
          reduceOnly,
          tpSlData: tpSlDataRef.current,
        })
        .then(() => {
          setSizeState(BN.ZERO);
          setTotalState(BN.ZERO);
          setSliderState(0);
          setTpSlData({
            takeProfitPrice: BN.ZERO,
            gainPercent: BN.ZERO,
            stopLossPrice: BN.ZERO,
            lossPercent: BN.ZERO,
          });
        });
    },
    [tradeboxStore, marketStore],
  );

  useEffect(() => {
    return () => {
      tradeboxStore.setChartTpSlPreview(null);
    };
  }, [tradeboxStore]);

  return {
    limitPrice,
    size,
    total,
    direction,
    slider,
    userTradeableBalance,
    userLeveragableBalance,
    availableMargin,
    leverage: currentLeverage,
    setLimitPrice,
    setSize,
    setTotal,
    setDirection,
    setSlider,
    setLeverage,
    validate,
    orderType,
    setOrderType,
    create,
    isLeverageDialogOpen,
    setIsLeverageDialogOpen,
    tpSlData,
    setTpSlData,
  };
};
